import type { Availability } from '../../core/model/availability';
import type { Restaurant, Review } from '../../core/model/restaurant';
import type { SearchCriteria } from '../../core/model/search';
import { AppErrors } from '../../core/error/appError';
import { failure, success, type AppResult } from '../../core/result/appResult';
import type { LocalDate, LocalTime } from '../../core/util/datetime';
import type { ApiService } from '../api/apiService';
import { availabilityToDomain, restaurantToDomain, reviewToDomain } from '../mappers';
import { execute } from './apiCall';
import type { HomeCollection, Page, RestaurantRepository } from './repositories';

/**
 * Traduce el contrato de red al dominio.
 *
 * Los registros que el mapper descarta (un restaurante sin nombre, una reseña con calificación
 * imposible) **no rompen la lista**: se caen en silencio y el resto se muestra. Un dato malo de
 * ocho no debe dejar la pantalla en blanco.
 */
export class DefaultRestaurantRepository implements RestaurantRepository {
  constructor(private readonly api: ApiService) {}

  async getCollection(
    collection: HomeCollection,
    locationId: string | null,
  ): Promise<AppResult<Restaurant[]>> {
    const result = await execute(() => this.api.getRestaurants({ collection, locationId }));
    if (!result.ok) return result;
    return success(mapRestaurants(result.data.items ?? []));
  }

  async getRestaurant(id: string): Promise<AppResult<Restaurant>> {
    const result = await execute(() => this.api.getRestaurant(id));
    if (!result.ok) return result;
    const restaurant = restaurantToDomain(result.data);
    // Un detalle que no se puede presentar equivale a un recurso que no existe: se trata como
    // 404 y la pantalla muestra su estado vacío, no un error alarmante.
    return restaurant ? success(restaurant) : failure(AppErrors.notFound());
  }

  async getReviews(id: string, page = 0): Promise<AppResult<Page<Review>>> {
    const result = await execute(() => this.api.getReviews(id, page));
    if (!result.ok) return result;
    const items = (result.data.items ?? [])
      .map(reviewToDomain)
      .filter((r): r is Review => r !== null);
    return success({ items, hasMore: result.data.has_more ?? false, total: result.data.total ?? items.length });
  }

  async search(criteria: SearchCriteria, page = 0): Promise<AppResult<Page<Restaurant>>> {
    const coordinates = criteria.location.kind === 'COORDINATES' ? criteria.location : null;
    const result = await execute(() =>
      this.api.searchRestaurants({
        query: criteria.query.trim() || null,
        locationId: criteria.location.kind === 'NAMED' ? criteria.location.id : null,
        latitude: coordinates?.latitude ?? null,
        longitude: coordinates?.longitude ?? null,
        cuisines: criteria.filters.cuisines,
        priceLevels: criteria.filters.priceLevels,
        minRating: criteria.filters.minRating,
        maxDistanceMeters: criteria.filters.maxDistanceMeters,
        amenities: criteria.filters.amenities,
        onlyAvailable: criteria.filters.onlyWithAvailability,
        date: criteria.date,
        time: criteria.time,
        partySize: criteria.partySize,
        sort: criteria.sort,
        page,
      }),
    );
    if (!result.ok) return result;
    const items = mapRestaurants(result.data.items ?? []);
    return success({ items, hasMore: result.data.has_more ?? false, total: result.data.total ?? items.length });
  }

  async getAvailability(
    restaurantId: string,
    date: LocalDate,
    time: LocalTime,
    partySize: number,
  ): Promise<AppResult<Availability>> {
    const result = await execute(() => this.api.getAvailability(restaurantId, date, time, partySize));
    if (!result.ok) return result;
    return success(availabilityToDomain(result.data, restaurantId, date, partySize));
  }
}

function mapRestaurants(items: readonly Parameters<typeof restaurantToDomain>[0][]): Restaurant[] {
  return items.map(restaurantToDomain).filter((r): r is Restaurant => r !== null);
}
